import styled from "@emotion/styled";
import { Link } from "react-router-dom";
import { CardActionArea, Typography } from "@avenue-8/sidekick-ui";

export {
  ContactName,
  ViewContactHint,
  FailedIcon as MiniFailedIcon,
  ScoreBadge,
  ScoreBadgeText,
} from "@modules/radar/shared/components/SignalCardPrimitives.styles";

// Matches the wrapping pattern already established in
// ExplorePrompts/CategoryCards.tsx - Swiper's own root class needs an
// explicit width (its base CSS doesn't set one), and slides need their
// height forced to the card's natural height instead of Swiper's default.
export const SwiperRow = styled.div`
  width: 100%;

  .swiper {
    width: 100%;
  }

  .swiper-slide {
    height: auto !important;
    display: flex;
  }
`;

export const ScrollRow = styled.div`
  display: flex;
  gap: 1rem;
  overflow-x: auto;
  scroll-snap-type: x proximity;
  padding-bottom: 0.5rem;
  margin: 0 -0.25rem;
  padding-left: 0.25rem;
  padding-right: 0.25rem;

  &::-webkit-scrollbar {
    height: 6px;
  }

  ${(p) => p.theme.media.laptopUp} {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    overflow-x: visible;
    padding: 0;
    margin: 0;
  }
`;

// $inSwiper: below laptopUp, cards render inside a Swiper slide instead of
// ScrollRow's flex/grid, which sizes its own slot - the card just fills it.
// A fixed height (not max-height) keeps every mobile card the same size
// regardless of content length; SignalCardContent below fades whatever
// overflows, while MiniBottomRow stays a plain flex sibling so the footer is
// always fully visible, pinned to the bottom.
export const SpotlightCard = styled.div<{ $inSwiper?: boolean }>`
  border: 1px solid ${(p) => p.theme.colors.border2};
  border-radius: 0.75rem;
  background-color: ${(p) => p.theme.colors.background};

  ${(p) =>
    p.$inSwiper
      ? `
    width: 100%;
    height: 320px;
    min-width: 0;
    overflow: hidden;
  `
      : `
    flex: 0 0 300px;
    scroll-snap-align: start;

    ${p.theme.media.laptopUp} {
      flex-basis: auto;
    }
  `}
`;

// Same sizing as SpotlightCard, without the card chrome - the Skeleton
// inside already reads as a card, so a border+background here would double up.
export const SkeletonCard = styled.div<{ $inSwiper?: boolean }>`
  ${(p) =>
    p.$inSwiper
      ? `
    width: 100%;
    height: 100%;
    min-width: 0;
  `
      : `
    flex: 0 0 300px;

    ${p.theme.media.laptopUp} {
      flex-basis: auto;
    }
  `}
`;

// min-width: 0 lets long headline/insight text wrap within the card instead
// of forcing the whole flex chain (up through the Swiper slide) wider to fit
// it unwrapped - matters below laptopUp now that MiniHeadline/MiniInsight no
// longer clamp there.
export const SpotlightCardActionArea = styled(CardActionArea)`
  padding: 1rem;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  height: 100%;
  min-width: 0;
  text-align: left;

  &:hover [data-view-contact-hint] {
    text-decoration: underline;
  }
`;

// Wraps everything above MiniBottomRow so the fade only covers the content
// that can actually overflow the fixed mobile card height - MiniBottomRow is
// a plain flex sibling below it, always fully visible at the card's bottom.
export const SignalCardContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;

  ${(p) => `
    ${p.theme.media.laptopDown} {
      position: relative;
      overflow: hidden;

      &::after {
        content: "";
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        height: 2.5rem;
        background: linear-gradient(to bottom, transparent, ${p.theme.colors.background});
        pointer-events: none;
      }
    }
  `}
`;

export const MiniTopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  width: 100%;
  margin-bottom: 0.75rem;
`;

export const SourceIconWrapperLarge = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 2rem;
  height: 2rem;
  border-radius: 0.5rem;
  background-color: ${(p) => p.theme.colors.background5};
  color: ${(p) => p.theme.icon.colors.primary};

  svg {
    font-size: 1.125rem;
  }
`;

export const TimeText = styled(Typography)`
  color: ${(p) => p.theme.text.colors.secondary};
  white-space: nowrap;
`;

// Line-clamped so cards sharing a desktop grid row stay a uniform height -
// unnecessary below laptopUp, where each card gets its own full-width slide.
export const MiniHeadline = styled(Typography)`
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  margin-bottom: 0.25rem;
  line-height: 1.3;

  ${(p) => p.theme.media.laptopDown} {
    display: block;
    -webkit-line-clamp: unset;
    overflow: visible;
    overflow-wrap: break-word;
  }
`;

export const MiniInsight = styled(Typography)`
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;

  ${(p) => p.theme.media.laptopDown} {
    display: block;
    -webkit-line-clamp: unset;
    overflow: visible;
    overflow-wrap: break-word;
  }
`;

export const MiniStatusBlock = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  padding: 1.5rem 1rem;
  border-radius: 0.5rem;
  background-color: ${(p) => p.theme.colors.background2};
  color: ${(p) => p.theme.text.colors.secondary};
`;

export const MiniBottomRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  width: 100%;
  margin-top: auto;
  padding-top: 1rem;

  ${(p) => p.theme.media.laptopDown} {
    margin-top: 0.5rem;
  }
`;

export const PanelEmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 0.5rem;
  padding: 2rem 1rem;
  border: 1px solid ${(p) => p.theme.colors.border2};
  border-radius: 1rem;
  background-color: ${(p) => p.theme.colors.background};
`;

export const PanelEmptyStateIconWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  margin-bottom: 0.25rem;
  border-radius: 50%;
  background-color: ${(p) => p.theme.colors.background5};
  color: ${(p) => p.theme.text.colors.brand};
`;

export const ViewAllLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: ${(p) => p.theme.text.fontSizes.m};
  color: ${(p) => p.theme.text.colors.brand};
  font-weight: 600;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;
